/**
 * iter 72al — AR/AP Ledger.
 *
 * Business-workspace only. Full flat register of every AR/AP event
 * (invoices/bills, payments, credit memos, debit memos) across the whole
 * book. Two view modes:
 *   - "party": grouped by customer/vendor with opening/closing sub-balances
 *   - "none":  single chronological register with book-wide running balance
 *
 * Exports: CSV, PDF, Email — same three exports as Statement of Account.
 *
 * Kept as a single shared component (kind = "ar" | "ap") because the two
 * ledgers only differ in labels, colors, and endpoint prefix — the layout
 * and data shape are identical.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Mail, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { format, startOfYear } from "date-fns";
import { DateField, fmtISO, downloadStream, CsvButton } from "./_shared";

const KIND_META = {
  ar: {
    label: "AR Ledger",
    partyLabel: "Customer",
    endpointStem: "ar-ledger",
    accent: "text-[#2F6B4A]",
    accentBg: "bg-[#EAF4EE]",
    testPrefix: "ar-ledger",
  },
  ap: {
    label: "AP Ledger",
    partyLabel: "Vendor",
    endpointStem: "ap-ledger",
    accent: "text-[#B4283A]",
    accentBg: "bg-[#FCE7E9]",
    testPrefix: "ap-ledger",
  },
};

const fm = (n, ccy) => formatCurrency(Number(n || 0), ccy || "USD");

export function LedgerReport({ kind, currency }) {
  const meta = KIND_META[kind];
  const [start, setStart] = useState(startOfYear(new Date()));
  const [end, setEnd] = useState(new Date());
  const [groupBy, setGroupBy] = useState("party");
  // iter 72am — filters. `partyId` = single-party isolate (empty = all).
  // `kind` = single event-type filter (empty = all). `minAmount` = numeric
  // threshold string (empty = all). We keep filters as strings so a bare
  // number stays visible in the input while the user types.
  const [partyId, setPartyId] = useState("");
  const [eventKind, setEventKind] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [parties, setParties] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);

  // Load customers/vendors for the party filter dropdown.
  useEffect(() => {
    const partyPath = kind === "ar" ? "/customers" : "/vendors";
    api.get(partyPath).then(({ data }) => setParties(data || []))
       .catch(() => setParties([]));
  }, [kind]);

  const params = useMemo(() => {
    const p = { start: fmtISO(start), end: fmtISO(end), group_by: groupBy };
    if (partyId) p.party_ids = partyId;
    if (eventKind) p.kinds = eventKind;
    if (minAmount && !isNaN(parseFloat(minAmount))) p.min_amount = parseFloat(minAmount);
    return p;
  }, [start, end, groupBy, partyId, eventKind, minAmount]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${meta.endpointStem}`, { params });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't load ${meta.label}`);
    } finally {
      setLoading(false);
    }
  }, [params, meta]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      await downloadStream(
        `/reports/${meta.endpointStem}.pdf`, params,
        `${meta.endpointStem}_${params.start}_${params.end}.pdf`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't download PDF");
    }
  };

  const sendEmail = async () => {
    if (!emailTo) {
      toast.error("Please enter a recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/reports/${meta.endpointStem}/email`,
                                        { email: emailTo, ...params });
      if (data.delivery_mode === "queued_no_key") {
        toast.success(`Ledger queued (Resend key not configured — ${data.recipient})`);
      } else {
        toast.success(`Ledger emailed to ${data.recipient}`);
      }
      setEmailOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send email");
    } finally {
      setSending(false);
    }
  };

  if (!data && loading) {
    return <div className="text-[var(--muted-text)] text-sm p-6">Loading…</div>;
  }
  if (!data) return null;

  return (
    <div data-testid={`${meta.testPrefix}-report`} className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <DateField label="From" value={start} onChange={setStart}
                      testId={`${meta.testPrefix}-start`} />
          <DateField label="To" value={end} onChange={setEnd}
                      testId={`${meta.testPrefix}-end`} />
          <div className="space-y-1">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">View</div>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[220px] h-9" data-testid={`${meta.testPrefix}-groupby`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="party" data-testid={`${meta.testPrefix}-view-grouped`}>
                  Grouped by {meta.partyLabel.toLowerCase()}
                </SelectItem>
                <SelectItem value="none" data-testid={`${meta.testPrefix}-view-flat`}>
                  Flat chronological
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* iter 72am — filters */}
          <div className="space-y-1">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
              {meta.partyLabel}
            </div>
            <Select value={partyId || "all"}
                     onValueChange={(v) => setPartyId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px] h-9" data-testid={`${meta.testPrefix}-party`}>
                <SelectValue placeholder={`All ${meta.partyLabel.toLowerCase()}s`} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all" data-testid={`${meta.testPrefix}-party-all`}>
                  All {meta.partyLabel.toLowerCase()}s
                </SelectItem>
                {parties.map((p) => (
                  <SelectItem key={p.id} value={p.id}
                               data-testid={`${meta.testPrefix}-party-${p.id}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Event</div>
            <Select value={eventKind || "all"}
                     onValueChange={(v) => setEventKind(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] h-9" data-testid={`${meta.testPrefix}-kind`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                <SelectItem value={kind === "ar" ? "invoice" : "bill"}
                             data-testid={`${meta.testPrefix}-kind-charge`}>
                  {kind === "ar" ? "Invoices" : "Bills"} only
                </SelectItem>
                <SelectItem value="payment" data-testid={`${meta.testPrefix}-kind-payment`}>
                  Payments only
                </SelectItem>
                <SelectItem value="credit_memo">Credit memos only</SelectItem>
                <SelectItem value="debit_memo">Debit memos only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Min amount</div>
            <Input type="number" min="0" step="1" placeholder="0.00"
                    value={minAmount} onChange={(e) => setMinAmount(e.target.value)}
                    className="w-[120px] h-9"
                    data-testid={`${meta.testPrefix}-min-amount`} />
          </div>
          {(partyId || eventKind || minAmount) && (
            <Button type="button" variant="ghost" size="sm" className="h-9"
                     data-testid={`${meta.testPrefix}-clear-filters`}
                     onClick={() => { setPartyId(""); setEventKind(""); setMinAmount(""); }}>
              Clear filters
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CsvButton
            testId={`${meta.testPrefix}-csv`}
            onClick={() => downloadStream(`/reports/${meta.endpointStem}.csv`,
                                            params,
                                            `${meta.endpointStem}_${params.start}_${params.end}.csv`)}
          />
          <Button type="button" variant="outline" size="sm" className="h-9 gap-2"
                   data-testid={`${meta.testPrefix}-pdf`}
                   onClick={downloadPdf}>
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button type="button" size="sm" className="h-9 gap-2 btn-brand"
                   data-testid={`${meta.testPrefix}-email`}
                   onClick={() => setEmailOpen(true)}>
            <Mail className="w-3.5 h-3.5" /> Email
          </Button>
        </div>
      </div>

      {/* Grand-total banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Opening", data.grand_opening],
          ["Charges", data.grand_charges],
          ["Payments / Credits", data.grand_credits],
          ["Closing", data.grand_closing],
        ].map(([label, val]) => (
          <div key={label} className={`card-flat px-4 py-3 ${label === "Closing" ? meta.accentBg : ""}`}
                data-testid={`${meta.testPrefix}-total-${label.toLowerCase().split(" ")[0]}`}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">{label}</div>
            <div className={`text-lg font-semibold font-mono ${label === "Closing" ? meta.accent : ""}`}>
              {fm(val, currency)}
            </div>
          </div>
        ))}
      </div>

      {/* Body */}
      {data.group_by === "party" ? (
        <div className="space-y-3">
          {(data.groups || []).length === 0 ? (
            <div className="card-flat text-center text-sm text-[var(--muted-text)] p-10">
              No activity in this period.
            </div>
          ) : (
            (data.groups || []).map((g) => {
              const isOpen = !!expanded[g.party_id];
              return (
                <div key={g.party_id} className="card-flat overflow-hidden"
                      data-testid={`${meta.testPrefix}-group-${g.party_id}`}>
                  <button type="button"
                           className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[var(--hover)]"
                           onClick={() => setExpanded({ ...expanded, [g.party_id]: !isOpen })}
                           data-testid={`${meta.testPrefix}-group-toggle-${g.party_id}`}>
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium">{g.party_name}</span>
                      <span className="text-xs text-[var(--muted-text)]">
                        · {g.activity.length} event{g.activity.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-[var(--muted-text)]">
                        Opening <span className="font-mono">{fm(g.opening_balance, currency)}</span>
                      </span>
                      <span className={`font-mono font-semibold ${meta.accent}`}>
                        {fm(g.closing_balance, currency)}
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <table className="w-full text-sm border-t border-[var(--divider)]">
                      <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
                        <tr>
                          <th className="text-left px-4 py-2">Date</th>
                          <th className="text-left px-4 py-2">Ref</th>
                          <th className="text-left px-4 py-2">Description</th>
                          <th className="text-right px-4 py-2">Debit</th>
                          <th className="text-right px-4 py-2">Credit</th>
                          <th className="text-right px-4 py-2">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="text-xs italic bg-[var(--hover)]">
                          <td className="px-4 py-1.5" colSpan={5}>Opening balance</td>
                          <td className="px-4 py-1.5 text-right font-mono">{fm(g.opening_balance, currency)}</td>
                        </tr>
                        {g.activity.map((r, idx) => (
                          <tr key={`${g.party_id}-${idx}`}
                               className="border-t border-[var(--divider)]"
                               data-testid={`${meta.testPrefix}-row-${g.party_id}-${idx}`}>
                            <td className="px-4 py-2 whitespace-nowrap">{r.date}</td>
                            <td className="px-4 py-2 font-mono text-xs">{r.reference}</td>
                            <td className="px-4 py-2">{r.description}</td>
                            <td className="px-4 py-2 text-right font-mono">
                              {r.debit ? fm(r.debit, r.currency) : ""}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {r.credit ? fm(r.credit, r.currency) : ""}
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-semibold">
                              {fm(r.running_balance, r.currency)}
                            </td>
                          </tr>
                        ))}
                        <tr className="text-xs bg-[var(--hover)] font-semibold">
                          <td className="px-4 py-1.5" colSpan={3}>Closing balance</td>
                          <td className="px-4 py-1.5 text-right font-mono">{fm(g.total_charges, currency)}</td>
                          <td className="px-4 py-1.5 text-right font-mono">{fm(g.total_credits, currency)}</td>
                          <td className="px-4 py-1.5 text-right font-mono">{fm(g.closing_balance, currency)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          {(data.activity || []).length === 0 ? (
            <div className="text-center text-sm text-[var(--muted-text)] p-10">
              No activity in this period.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">{meta.partyLabel}</th>
                  <th className="text-left px-4 py-2">Ref</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="text-right px-4 py-2">Debit</th>
                  <th className="text-right px-4 py-2">Credit</th>
                  <th className="text-right px-4 py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-xs italic bg-[var(--hover)]">
                  <td className="px-4 py-1.5" colSpan={6}>Opening balance</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fm(data.grand_opening, currency)}</td>
                </tr>
                {data.activity.map((r, idx) => (
                  <tr key={idx} className="border-t border-[var(--divider)]"
                       data-testid={`${meta.testPrefix}-flat-row-${idx}`}>
                    <td className="px-4 py-2 whitespace-nowrap">{r.date}</td>
                    <td className="px-4 py-2">{r.party_name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.reference}</td>
                    <td className="px-4 py-2">{r.description}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {r.debit ? fm(r.debit, r.currency) : ""}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {r.credit ? fm(r.credit, r.currency) : ""}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">
                      {fm(r.running_balance, r.currency)}
                    </td>
                  </tr>
                ))}
                <tr className="text-xs bg-[var(--hover)] font-semibold">
                  <td className="px-4 py-1.5" colSpan={4}>Closing balance</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fm(data.grand_charges, currency)}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fm(data.grand_credits, currency)}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{fm(data.grand_closing, currency)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid={`${meta.testPrefix}-email-dialog`} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email {meta.label}</DialogTitle>
            <DialogDescription>
              {params.start} → {params.end} · {groupBy === "party" ? "Grouped" : "Flat"}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Recipient email</Label>
            <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="you@company.com"
                    data-testid={`${meta.testPrefix}-email-input`} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} className="btn-brand"
                     data-testid={`${meta.testPrefix}-email-send`}>
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
